import axios from "axios";
import { ENV } from "../constants";
import { fetchAuthSession } from 'aws-amplify/auth';
 
// 開発用
//const API_BASE_URL = 'http://127.0.0.1:8001';
const API_BASE_URL = ENV.API_URL;
 
export const apiClient = axios.create({
    baseURL: API_BASE_URL,
    responseType: "json",
});
 
 // Attach Cognito token (always send 'Authorization: Bearer <token>'; token_use selectable via env)
 apiClient.interceptors.request.use(async (config) => {
     try {
         // If Authorization already provided, do not override
         const hdrs: any = config.headers ?? {};
         const hasAuthHeader = !!hdrs.Authorization || !!hdrs.authorization;
         if (!hasAuthHeader) {
             const { tokens } = await fetchAuthSession();
             const idToken = tokens?.idToken?.toString();
             const accessToken = tokens?.accessToken?.toString();
             const mode = (process.env.REACT_APP_COGNITO_AUTH_TOKEN_USE as 'id' | 'access' | 'auto' | undefined) ?? 'auto';
             const token =
                 mode === 'id' ? idToken :
                 mode === 'access' ? accessToken :
                 (idToken || accessToken);
             if (token) {
                 if (hdrs && typeof hdrs.set === 'function') {
                     // AxiosHeaders instance
                     hdrs.set('Authorization', `Bearer ${token}`);
                 } else {
                     config.headers = {
                         ...(config.headers as any ?? {}),
                         Authorization: `Bearer ${token}`,
                     };
                 }
             }
         }
 
         // Add Content-Type only for methods with a body to avoid unnecessary CORS preflight on GET/HEAD
         const method = String(config.method || 'get').toLowerCase();
         const wantsBody = ['post', 'put', 'patch', 'delete'].includes(method);
         const hasContentType = !!hdrs['Content-Type'] || !!hdrs['content-type'];
         if (wantsBody && !hasContentType) {
             if (hdrs && typeof hdrs.set === 'function') {
                 hdrs.set('Content-Type', 'application/json');
             } else {
                 config.headers = {
                     ...(config.headers as any ?? {}),
                     'Content-Type': 'application/json',
                 };
             }
         }
     } catch {
         // Silently continue without Authorization if token fetch fails
     }
     return config;
 });
 
 // 401/400 応答時のフォールバック:
 //   - まずプレーン送信（Authorization: <token>）で通る構成向けに送信
 //   - オーソライザ or 統合側が Bearer を要求する構成では 401/400 が返る可能性があるため
 //     一度だけ Bearer 付きで自動リトライする
 apiClient.interceptors.response.use(
     (resp) => resp,
     async (error) => {
         try {
             const status = error?.response?.status;
             const cfg: any = error?.config;
             if (!cfg) return Promise.reject(error);
 
             // 既に Bearer で再試行済みなら打ち切り
             if (cfg.__retriedWithBearer) return Promise.reject(error);
 
             // 送信時の Authorization 値（AxiosHeaders 互換）を取得
             const getAuthHeader = (headers: any): string | undefined => {
                 if (!headers) return undefined;
                 if (typeof headers.get === 'function') return headers.get('Authorization');
                 return headers.Authorization || headers.authorization;
             };
             const currentAuth = getAuthHeader(cfg.headers);
             const sentPlainToken = !!currentAuth && !/^Bearer\s+/i.test(String(currentAuth));
 
             // 401（オーソライザNG）または 400（統合側が Bearer 前提のパースで失敗）のとき、
             // かつ最初にプレーンで送っていた場合のみ Bearer 付きで一度だけ再試行
             if ((status === 401 || status === 400) && sentPlainToken) {
                 const { tokens } = await fetchAuthSession();
                 const accessToken = tokens?.accessToken?.toString();
                 const idToken = tokens?.idToken?.toString();
                 const token = idToken || accessToken;
                 if (token) {
                     cfg.__retriedWithBearer = true;
                     if (cfg.headers && typeof (cfg.headers as any).set === 'function') {
                         (cfg.headers as any).set('Authorization', `Bearer ${token}`);
                     } else {
                         cfg.headers = { ...(cfg.headers ?? {}), Authorization: `Bearer ${token}` };
                     }
                     return apiClient(cfg);
                 }
             }
         } catch {
             // noop
         }
         return Promise.reject(error);
     }
 );

// Re-exports to avoid importing axios in call sites
export { isAxiosError as isApiError } from "axios";
export type { AxiosError } from "axios";